import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Divider from '@mui/material/Divider';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import IconButton from '@mui/material/IconButton';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import dayjs from 'dayjs';

import { DataTable, StatusBadge, EmptyState, ActionMenu } from '../../../components/shared';
import { suppliers, parts, partOrders, partReplenishmentOrders, purchaseOrders as poArray } from '../../../services/dataClient';
import { fieldSx } from '../../../theme/fieldStyles';

const fmt = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n ?? 0);

const activeSuppliers = suppliers.filter((s) => s.active);

const PRESETS = [
  { label: 'This Month',    value: 'thisMonth' },
  { label: 'Last Month',    value: 'lastMonth' },
  { label: 'Last 3 Months', value: 'last3'     },
  { label: 'Last 6 Months', value: 'last6'     },
  { label: 'Year to Date',  value: 'ytd'       },
  { label: 'Custom Range',  value: 'custom'    },
];

const EMPTY_LINE = { partNumber: '', partDescription: '', quantity: '1', unitCost: '' };

// ─── Tab-level summary bar ────────────────────────────────────────────────────
function TabSummaryBar({ pos }) {
  const stats = [
    { label: 'Total POs',  value: pos.length },
    { label: 'Draft',      value: pos.filter((p) => p.status === 'Draft').length },
    { label: 'Submitted',  value: pos.filter((p) => p.status === 'Submitted').length },
    { label: 'Received',   value: pos.filter((p) => p.status === 'Received').length },
  ];
  return (
    <Box sx={{ display: 'flex', gap: 3, px: 3, py: 2, borderBottom: '1px solid var(--grey-200)' }}>
      {stats.map((s) => (
        <Box key={s.label} sx={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
          <Typography sx={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--grey-900)' }}>
            {s.value}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>{s.label}</Typography>
        </Box>
      ))}
    </Box>
  );
}

// ─── Section label ─────────────────────────────────────────────────────────────
function SectionLabel({ children }) {
  return (
    <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: 'var(--grey-500)', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.25 }}>
      {children}
    </Typography>
  );
}

// ─── PO Modal (create + edit) ─────────────────────────────────────────────────
function POModal({ open, mode, initial, onClose, onSave }) {
  const [linkedOrderType, setLinkedOrderType] = useState('none');
  const [linkedOrderId,   setLinkedOrderId]   = useState('');
  const [vendorName,      setVendorName]      = useState('');
  const [lineItems,       setLineItems]       = useState([{ ...EMPTY_LINE }]);
  const [notes,           setNotes]           = useState('');

  const handleEnter = () => {
    if (mode === 'edit' && initial) {
      // Detect existing linked order type from ticket number prefix
      const tn = initial.linkedTicketNumber ?? '';
      const detectedType = tn.startsWith('REP') ? 'replenishment' : tn.startsWith('INC') ? 'partOrder' : 'none';
      setLinkedOrderType(detectedType);
      setLinkedOrderId(initial.linkedOrderId ?? '');
      setVendorName(initial.vendorName ?? '');
      setLineItems(
        initial.lineItems?.length
          ? initial.lineItems.map((li) => ({
              partNumber:      li.partNumber      ?? '',
              partDescription: li.partDescription ?? '',
              quantity:        String(li.quantity  ?? 1),
              unitCost:        li.unitCost != null ? String(li.unitCost) : '',
            }))
          : [{
              partNumber:      initial.partNumber      ?? '',
              partDescription: initial.partDescription ?? '',
              quantity:        String(initial.quantity  ?? 1),
              unitCost:        initial.unitCost != null ? String(initial.unitCost) : '',
            }]
      );
      setNotes(initial.notes ?? '');
    } else {
      setLinkedOrderType('none');
      setLinkedOrderId('');
      setVendorName('');
      setLineItems([{ ...EMPTY_LINE }]);
      setNotes('');
    }
  };

  // ── Line item helpers ────────────────────────────────────────────────────────
  const updateLine = (idx, field, value) =>
    setLineItems((prev) => prev.map((li, i) => i === idx ? { ...li, [field]: value } : li));

  const handlePartChange = (idx, value) => {
    const desc = parts.find((p) => p.partNumber === value)?.description ?? '';
    setLineItems((prev) =>
      prev.map((li, i) => i === idx ? { ...li, partNumber: value, partDescription: desc } : li)
    );
  };

  const addLine    = () => setLineItems((prev) => [...prev, { ...EMPTY_LINE }]);
  const removeLine = (idx) => setLineItems((prev) => prev.filter((_, i) => i !== idx));

  // ── Validation ───────────────────────────────────────────────────────────────
  const isValid =
    vendorName.trim() !== '' &&
    lineItems.every((li) => li.partNumber && parseInt(li.quantity, 10) >= 1) &&
    (linkedOrderType === 'none' || Boolean(linkedOrderId));

  // ── Save ─────────────────────────────────────────────────────────────────────
  const handleSave = () => {
    const builtLineItems = lineItems.map((li) => ({
      partNumber:      li.partNumber,
      partDescription: li.partDescription,
      quantity:        parseInt(li.quantity, 10) || 1,
      unitCost:        li.unitCost !== '' ? parseFloat(li.unitCost) : null,
    }));
    const totalCost = builtLineItems.reduce(
      (sum, li) => sum + (li.unitCost ?? 0) * li.quantity, 0
    );

    if (mode === 'edit') {
      // Resolve linked order for edit too
      let editLinkedOrderId = null, editLinkedTicketNumber = null;
      if (linkedOrderType === 'partOrder') {
        const o = partOrders.find((o) => o.id === linkedOrderId);
        editLinkedOrderId      = o?.id ?? null;
        editLinkedTicketNumber = o?.ticketNumber ?? null;
      } else if (linkedOrderType === 'replenishment') {
        const o = partReplenishmentOrders.find((o) => o.id === linkedOrderId);
        editLinkedOrderId      = o?.id ?? null;
        editLinkedTicketNumber = o?.orderNumber ?? null;
      }
      onSave({
        vendorName,
        quantity:          builtLineItems.reduce((s, li) => s + li.quantity, 0),
        totalCost,
        lineItems:         builtLineItems,
        notes:             notes || null,
        linkedOrderId:     editLinkedOrderId,
        linkedTicketNumber: editLinkedTicketNumber,
      });
      return;
    }

    // Resolve linked order
    let linkedOrderIdVal = null, linkedTicketNumber = null;
    if (linkedOrderType === 'partOrder') {
      const o = partOrders.find((o) => o.id === linkedOrderId);
      linkedOrderIdVal   = o?.id ?? null;
      linkedTicketNumber = o?.ticketNumber ?? null;
    } else if (linkedOrderType === 'replenishment') {
      const o = partReplenishmentOrders.find((o) => o.id === linkedOrderId);
      linkedOrderIdVal   = o?.id ?? null;
      linkedTicketNumber = o?.orderNumber ?? null;
    }

    const newPO = {
      id:              `po-new-${Date.now()}`,
      poNumber:        `PO-${6000 + poArray.length + 1}`,
      vendorName,
      quantity:        builtLineItems.reduce((s, li) => s + li.quantity, 0),
      totalCost,
      lineItems:       builtLineItems,
      status:          'Draft',
      date:            dayjs().format('YYYY-MM-DD'),
      notes:           notes || null,
      linkedOrderId:   linkedOrderIdVal,
      linkedTicketNumber,
    };

    poArray.unshift(newPO);
    onSave(newPO);
  };

  const lineTotals = lineItems.map(
    (li) => (parseFloat(li.unitCost) || 0) * (parseInt(li.quantity, 10) || 0)
  );
  const grandTotal = lineTotals.reduce((a, b) => a + b, 0);

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => { if (reason !== 'backdropClick') onClose(); }}
      maxWidth="md"
      fullWidth
      TransitionProps={{ onEnter: handleEnter }}
    >
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {mode === 'edit' ? 'Edit Purchase Order' : 'Create Purchase Order'}
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 0, pt: '12px !important' }}>

        {/* ── Linked Order ── */}
        <SectionLabel>Linked Order (optional)</SectionLabel>
        <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
          <FormControl size="small" sx={{ minWidth: 200, ...fieldSx }}>
            <InputLabel>Order Type</InputLabel>
            <Select
              label="Order Type"
              value={linkedOrderType}
              onChange={(e) => { setLinkedOrderType(e.target.value); setLinkedOrderId(''); }}
            >
              <MenuItem value="none"          sx={{ fontSize: '0.875rem' }}>None</MenuItem>
              <MenuItem value="partOrder"     sx={{ fontSize: '0.875rem' }}>Part Order</MenuItem>
              <MenuItem value="replenishment" sx={{ fontSize: '0.875rem' }}>Part Replenishment</MenuItem>
            </Select>
          </FormControl>

          {linkedOrderType === 'partOrder' && (
            <FormControl size="small" fullWidth sx={fieldSx}>
              <InputLabel>Part Order</InputLabel>
              <Select
                label="Part Order"
                value={linkedOrderId}
                onChange={(e) => setLinkedOrderId(e.target.value)}
              >
                {partOrders.map((o) => (
                  <MenuItem key={o.id} value={o.id} sx={{ fontSize: '0.875rem' }}>
                    {o.ticketNumber} — {o.customerName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {linkedOrderType === 'replenishment' && (
            <FormControl size="small" fullWidth sx={fieldSx}>
              <InputLabel>Replenishment Order</InputLabel>
              <Select
                label="Replenishment Order"
                value={linkedOrderId}
                onChange={(e) => setLinkedOrderId(e.target.value)}
              >
                {partReplenishmentOrders.map((o) => (
                  <MenuItem key={o.id} value={o.id} sx={{ fontSize: '0.875rem' }}>
                    {o.orderNumber} — {o.stockLocation}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
        <Divider sx={{ mb: 2.5 }} />

        {/* ── Supplier ── */}
        <SectionLabel>Supplier *</SectionLabel>
        <FormControl size="small" fullWidth sx={{ ...fieldSx, mb: 2.5 }}>
          <InputLabel>Supplier</InputLabel>
          <Select label="Supplier" value={vendorName} onChange={(e) => setVendorName(e.target.value)}>
            {activeSuppliers.map((s) => (
              <MenuItem key={s.id} value={s.name} sx={{ fontSize: '0.875rem' }}>{s.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Divider sx={{ mb: 2.5 }} />

        {/* ── Line Items ── */}
        <SectionLabel>Parts *</SectionLabel>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 1 }}>
          {lineItems.map((li, idx) => {
            const rowTotal = (parseFloat(li.unitCost) || 0) * (parseInt(li.quantity, 10) || 0);
            return (
              <Box key={idx} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                {/* Part # */}
                <FormControl size="small" required sx={{ flex: '1.2 1 0', ...fieldSx }}>
                  <InputLabel>Part #</InputLabel>
                  <Select
                    label="Part #"
                    value={li.partNumber}
                    onChange={(e) => handlePartChange(idx, e.target.value)}
                  >
                    {parts.map((p) => (
                      <MenuItem key={p.partNumber} value={p.partNumber} sx={{ fontSize: '0.875rem' }}>
                        {p.partNumber}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Description */}
                <TextField
                  size="small"
                  label="Description"
                  value={li.partDescription}
                  onChange={(e) => updateLine(idx, 'partDescription', e.target.value)}
                  sx={{ flex: '1.8 1 0', ...fieldSx }}
                />

                {/* Qty */}
                <TextField
                  size="small"
                  label="Qty"
                  required
                  type="number"
                  value={li.quantity}
                  onChange={(e) => updateLine(idx, 'quantity', e.target.value)}
                  inputProps={{ min: 1 }}
                  sx={{ flex: '0.5 1 0', minWidth: 64, ...fieldSx }}
                />

                {/* Unit Cost */}
                <TextField
                  size="small"
                  label="Unit Cost ($)"
                  type="number"
                  value={li.unitCost}
                  onChange={(e) => updateLine(idx, 'unitCost', e.target.value)}
                  inputProps={{ min: 0, step: '0.01' }}
                  helperText={rowTotal > 0 ? fmt(rowTotal) : ''}
                  sx={{ flex: '0.8 1 0', minWidth: 100, ...fieldSx }}
                />

                {/* Delete */}
                <Box sx={{ pt: '4px' }}>
                  <IconButton
                    size="small"
                    onClick={() => removeLine(idx)}
                    disabled={lineItems.length === 1}
                    sx={{ color: lineItems.length === 1 ? 'transparent' : 'var(--grey-400)', '&:hover': { color: '#d32f2f' } }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              </Box>
            );
          })}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={addLine}
            sx={{ color: '#009688', textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', p: 0, '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' } }}
          >
            Add Part
          </Button>
          {grandTotal > 0 && (
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-600)' }}>
              Total: <strong>{fmt(grandTotal)}</strong>
            </Typography>
          )}
        </Box>
        <Divider sx={{ mb: 2.5 }} />

        {/* ── Notes ── */}
        <TextField
          size="small"
          label="Notes / Special Instructions (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          rows={2}
          fullWidth
          sx={fieldSx}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Typography
          onClick={onClose}
          sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-700)' } }}
        >
          Cancel
        </Typography>
        <Button
          variant="contained"
          size="small"
          disabled={!isValid}
          onClick={handleSave}
          sx={{ backgroundColor: '#009688', '&:hover': { backgroundColor: '#00796B' }, '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' }, textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', boxShadow: 'none', px: 2 }}
        >
          {mode === 'edit' ? 'Save Changes' : 'Create PO'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Tab ──────────────────────────────────────────────────────────────────────
export default function PurchaseOrdersTab({
  pos, onUpdate, initialSearch = '',
  preset, onPresetChange,
  customStart, onCustomStartChange,
  customEnd, onCustomEndChange,
}) {
  const navigate = useNavigate();
  const [search,     setSearch]     = useState(initialSearch);
  const [sortConfig, setSortConfig] = useState({ field: 'date', direction: 'desc' });
  const [modalOpen,  setModalOpen]  = useState(false);
  const [modalMode,  setModalMode]  = useState('create');
  const [editTarget, setEditTarget] = useState(null);

  const handleSort = (field) => setSortConfig((prev) => ({
    field, direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
  }));

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...pos]
      .filter((p) =>
        !q ||
        p.poNumber.toLowerCase().includes(q) ||
        p.vendorName.toLowerCase().includes(q) ||
        (p.lineItems ?? []).some((li) => (li.partNumber ?? '').toLowerCase().includes(q)),
      )
      .sort((a, b) => {
        const av = a[sortConfig.field] ?? '';
        const bv = b[sortConfig.field] ?? '';
        const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      });
  }, [pos, search, sortConfig]);

  const handleSave = (data) => {
    if (modalMode === 'edit') {
      onUpdate((prev) => prev.map((p) => p.id === editTarget.id ? { ...p, ...data } : p));
    } else {
      // data is already the fully-built newPO (poArray already mutated inside POModal)
      onUpdate((prev) => [data, ...prev.filter((p) => p.id !== data.id)]);
    }
    setModalOpen(false);
  };

  const handleStatusChange = (po, newStatus) => {
    onUpdate((prev) => prev.map((p) => p.id === po.id ? { ...p, status: newStatus } : p));
  };

  const columns = [
    {
      field: 'poNumber',
      headerName: 'PO #',
      sortable: true,
      renderCell: (row) => (
        <Typography
          onClick={(e) => { e.stopPropagation(); navigate(`/accounting/purchase-orders/${row.id}`); }}
          sx={{ fontSize: '0.875rem', color: '#009688', fontWeight: 500, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
        >
          {row.poNumber}
        </Typography>
      ),
    },
    { field: 'vendorName', headerName: 'Supplier', sortable: true },
    {
      field: 'partNumber',
      headerName: 'Part #',
      sortable: true,
      renderCell: (row) => {
        const firstPart = row.lineItems?.length > 0 ? row.lineItems[0].partNumber : row.partNumber;
        const extra     = row.lineItems?.length > 1 ? row.lineItems.length - 1 : 0;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography sx={{ fontSize: '0.875rem' }}>{firstPart}</Typography>
            {extra > 0 && (
              <Box sx={{ px: '5px', py: '1px', borderRadius: '4px', backgroundColor: 'var(--grey-200)', display: 'inline-flex', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--grey-600)' }}>+{extra} more</Typography>
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      field: 'quantity',
      headerName: 'Quantity',
      sortable: true,
      renderCell: (row) => {
        const total = row.lineItems?.length > 0
          ? row.lineItems.reduce((s, li) => s + (li.quantity ?? 0), 0)
          : (row.quantity ?? 0);
        return <Typography sx={{ fontSize: '0.875rem' }}>{total}</Typography>;
      },
    },
    {
      field: 'totalCost',
      headerName: 'Total Cost',
      sortable: true,
      renderCell: (row) => (
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>{fmt(row.totalCost)}</Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      sortable: true,
      renderCell: (row) => <StatusBadge status={row.status} />,
    },
    { field: 'date', headerName: 'Date', sortable: true },
    {
      field: 'linkedTicketNumber',
      headerName: 'Linked Order',
      sortable: false,
      renderCell: (row) => {
        if (!row.linkedTicketNumber) {
          return <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-400)' }}>—</Typography>;
        }
        const isRep = row.linkedTicketNumber.startsWith('REP');
        const href  = isRep
          ? `/orders/part-replenishment/${row.linkedOrderId}`
          : `/orders/parts/${row.linkedOrderId}`;
        return (
          <Typography
            onClick={(e) => { e.stopPropagation(); navigate(href); }}
            sx={{ fontSize: '0.875rem', color: '#009688', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          >
            {row.linkedTicketNumber}
          </Typography>
        );
      },
    },
    {
      field: '_actions',
      headerName: '',
      sortable: false,
      renderCell: (row) => {
        const actions = [
          { label: 'View Detail', onClick: () => navigate(`/accounting/purchase-orders/${row.id}`) },
        ];
        if (row.status === 'Draft') {
          actions.push({ label: 'Edit PO',           onClick: () => { setEditTarget(row); setModalMode('edit'); setModalOpen(true); } });
          actions.push({ label: 'Mark as Submitted', onClick: () => handleStatusChange(row, 'Submitted') });
        }
        if (row.status === 'Submitted') {
          actions.push({ label: 'Mark as Received',  onClick: () => handleStatusChange(row, 'Received') });
        }
        return (
          <Box onClick={(e) => e.stopPropagation()}>
            <ActionMenu actions={actions} hideUntilRowHover />
          </Box>
        );
      },
    },
  ];

  return (
    <>
      <TabSummaryBar pos={pos} />

      {/* Search + filter + create */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'space-between' }}>
        <TextField
          size="small"
          placeholder="Search by PO #, Vendor, or Part…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'var(--grey-400)' }} /></InputAdornment> }}
          sx={{ minWidth: 400, maxWidth: 400, ...fieldSx }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Select
            size="small"
            value={preset ?? 'thisMonth'}
            onChange={(e) => onPresetChange?.(e.target.value)}
            sx={{ minWidth: 155, fontSize: '0.875rem', ...fieldSx['& .MuiOutlinedInput-root'], '& .MuiSelect-select': { paddingTop: '5px', paddingBottom: '5px' } }}
          >
            {PRESETS.map((p) => (
              <MenuItem key={p.value} value={p.value} sx={{ fontSize: '0.875rem' }}>{p.label}</MenuItem>
            ))}
          </Select>
          {preset === 'custom' && (
            <>
              <TextField size="small" type="date" label="From" value={customStart ?? ''}
                onChange={(e) => onCustomStartChange?.(e.target.value)}
                InputLabelProps={{ shrink: true }} sx={{ width: 145, ...fieldSx }} />
              <TextField size="small" type="date" label="To" value={customEnd ?? ''}
                onChange={(e) => onCustomEndChange?.(e.target.value)}
                InputLabelProps={{ shrink: true }} sx={{ width: 145, ...fieldSx }} />
            </>
          )}
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => { setModalMode('create'); setEditTarget(null); setModalOpen(true); }}
            sx={{ backgroundColor: '#009688', '&:hover': { backgroundColor: '#00796B' }, textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', boxShadow: 'none' }}
          >
            Create PO
          </Button>
        </Box>
      </Box>

      <Box sx={{ borderRadius: '8px', border: '1px solid var(--grey-200)', overflow: 'hidden' }}>
        {filtered.length === 0 ? (
          <EmptyState title="No purchase orders" description="Try adjusting your search or date range." />
        ) : (
          <DataTable columns={columns} data={filtered} sortConfig={sortConfig} onSort={handleSort} />
        )}
      </Box>

      <POModal
        open={modalOpen}
        mode={modalMode}
        initial={editTarget}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
      />
    </>
  );
}
